import { api } from '../../../api/client';
import type { AuditTrailRow } from './history';
import type {
  CancellationKey, CancellationQueueRow, CancellationReasonCode, CancellationResult,
} from './types';

// Backend: Api/Controllers/V1/Sampling/SampleCancellationController.cs.
// The shared axios client injects the JWT and drives the global loader (§18.6).

const BASE = '/api/v1/sample-cancellation';

/**
 * The queue for a Date Received window (ISO yyyy-MM-dd, inclusive; '' = open-ended).
 * Every other Query box cascades client-side over this set.  `silent` is for the
 * background fetch that feeds the Query boxes' option lists — it must not flash
 * the global loader on every date change.
 */
export async function fetchCancellationQueue(
  from: string, to: string, silent = false,
): Promise<CancellationQueueRow[]> {
  const res = await api.get<{ data: CancellationQueueRow[] }>(`${BASE}/queue`, {
    params: { from: from || undefined, to: to || undefined },
    ...(silent ? ({ silentLoading: true } as object) : {}),
  });
  return res.data.data ?? [];
}

export async function fetchReasonCodes(activeOnly: boolean): Promise<CancellationReasonCode[]> {
  const res = await api.get<{ data: CancellationReasonCode[] }>(`${BASE}/reason-codes`, {
    params: { activeOnly },
  });
  return res.data.data ?? [];
}

export async function cancelSamples(
  items: CancellationKey[], reasonCodeOid: string, comment: string,
): Promise<CancellationResult> {
  const res = await api.post<{ data: CancellationResult }>(`${BASE}/cancel`, { items, reasonCodeOid, comment });
  return res.data.data;
}

export async function restoreSamples(items: CancellationKey[], comment: string): Promise<CancellationResult> {
  const res = await api.post<{ data: CancellationResult }>(`${BASE}/restore`, { items, comment });
  return res.data.data;
}

/** Every audit row this form has written (most recent 2,000) — History is derived from it. */
export async function fetchCancellationAudit(): Promise<AuditTrailRow[]> {
  const res = await api.get<{ data: AuditTrailRow[] }>('/api/v1/audit/trail', {
    params: { formName: 'Sample Cancellation' },
  });
  return res.data.data ?? [];
}

/** The API's `{ error: { message } }` text when present, else the fallback. */
export function apiMessage(e: unknown, fallback: string): string {
  const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
  return msg || (e instanceof Error ? e.message : fallback);
}
